// GENERATED from messaging-platform/packages/chat-core/src/actions.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
import { composeFeatures } from './hub-types';
import type { ActionCard, AppointmentStatus, ConversationSummary, ConversationType, FilingView, PatientMention } from './hub-types';

/** Which app is asking: the clinical web, RelayMD, or MedicAI Connect (offered only what fits it). */
export type ActionApp = 'clinical' | 'relaymd' | 'connect';

/**
 * A chat action (tasks/chat-actions.md): `/task` and friends. Each opens a clinical page for the patient in play, by
 * way of the patient's file, which loads the patient and then moves to `page`.
 */
export interface ChatAction {
  id: string;
  /** what is typed and shown: "/task" */
  label: string;
  /** its name on the action sheet: "Add task" */
  title: string;
  /** one line on what it does, also searched */
  hint: string;
  /** the icon name each app maps to its own icon set */
  icon: string;
  needsPatient: boolean;
  apps: readonly ActionApp[];
  /** the clinical page the patient file opens for it (its patient-outlet path) */
  page: string;
}

/** Every action, in the order the picker lists them. Connect is offered none of these clinical ones. */
export const CHAT_ACTIONS: readonly ChatAction[] = [
  { id: 'appointment', label: '/appointment', title: 'Book appointment', hint: 'Book an appointment for the patient', icon: 'event', needsPatient: true, apps: ['clinical', 'relaymd'], page: 'add-appointment' },
  { id: 'note', label: '/note', title: 'Add note', hint: 'Write a clinical note for the patient', icon: 'note', needsPatient: true, apps: ['clinical', 'relaymd'], page: 'add-note' },
  { id: 'task', label: '/task', title: 'Add task', hint: 'Add a task or follow-up for the patient', icon: 'task', needsPatient: true, apps: ['clinical', 'relaymd'], page: 'add-task' },
];

/**
 * The `/query` the caret is in, or null: `/` at the start or after a space, then one word. A slash inside a word
 * ("and/or", "12/10") is not an action.
 */
export function slashQueryBefore(textBeforeCaret: string): { query: string; start: number } | null {
  const m = /(?:^|\s)\/([a-z-]{0,20})$/i.exec(textBeforeCaret);
  if (!m) return null;
  const query = m[1] ?? '';
  return { query, start: textBeforeCaret.length - query.length - 1 };
}

/** The actions an app offers, narrowed by the start of their name or of a word in their hint. */
export function searchActions(query: string, app: ActionApp): ChatAction[] {
  const q = query.trim().toLowerCase();
  return CHAT_ACTIONS.filter((a) => a.apps.includes(app)).filter((a) =>
    !q || a.id.startsWith(q) || a.hint.toLowerCase().split(/\s+/).some((w) => w.startsWith(q)),
  );
}

/**
 * The patients an action could be for, best first: a patient chat's own patient; else the `#patients` in the draft;
 * else whoever the conversation is being filed to. One means go ahead; none or several means ask with the # picker.
 */
export function actionPatients(
  conversation: Pick<ConversationSummary, 'type'> & Partial<Pick<ConversationSummary, 'patientRef' | 'name'>>,
  draftPatients: readonly PatientMention[],
  filing: FilingView | null,
): PatientMention[] {
  if (conversation.type === 'patient' && conversation.patientRef) return [{ patientRef: conversation.patientRef, title: conversation.name ?? 'this patient' }];
  if (draftPatients.length) return [...draftPatients];
  return (filing?.patients ?? []).filter((p) => p.patientRef).map((p) => ({ patientRef: p.patientRef!, title: p.name ?? 'this patient' }));
}

/** What the page is filled in with, and the conversation its card goes back to. */
export interface ActionPrefill {
  text?: string;
  /** clinical user ids, from the draft's @people */
  assignees?: readonly string[];
  chat?: string;
  /** the due date, YYYY-MM-DD (taskDueFrom) */
  due?: string;
  /** a booking's day (YYYY-MM-DD) and time (HH:mm), from appointmentWhenFrom */
  date?: string;
  time?: string;
  /** the clinical groupId of the conversation's practice: the page switches to it before it runs */
  practice?: string;
}

/**
 * The clinical path that runs an action: the patient's file, asked to open the action's page once the patient has
 * loaded (as `?linkDetails=1` does), with the prefill and the conversation to report back to.
 */
export function actionPath(actionId: string, patientId: string, prefill: ActionPrefill): string {
  const action = CHAT_ACTIONS.find((a) => a.id === actionId);
  if (!action) throw new Error(`unknown chat action ${actionId}`);
  const params = [`action=${encodeURIComponent(action.page)}`];
  if (prefill.text) params.push(`text=${encodeURIComponent(prefill.text)}`);
  if (prefill.assignees?.length) params.push(`assignees=${encodeURIComponent(prefill.assignees.join(','))}`);
  if (prefill.chat) params.push(`chat=${encodeURIComponent(prefill.chat)}`);
  if (prefill.due) params.push(`due=${encodeURIComponent(prefill.due)}`);
  if (prefill.date) params.push(`date=${encodeURIComponent(prefill.date)}`);
  if (prefill.time) params.push(`time=${encodeURIComponent(prefill.time)}`);
  if (prefill.practice) params.push(`practice=${encodeURIComponent(prefill.practice)}`);
  return `/patients/(patient:file/${encodeURIComponent(patientId)})?${params.join('&')}`;
}

/** An action card's title line: "Task for Jane Doe: Repeat bloods Friday". */
export function actionCardText(card: ActionCard): string {
  if (card.type === 'appointment') return `Appointment for ${card.patientName}: ${card.title}`;
  if (card.type === 'note') return `Note for ${card.patientName}: ${card.title}`;
  return `Task for ${card.patientName}: ${card.title}`;
}

/** Whether an action can run in a conversation, and why not when it cannot: the sheet shows it greyed with the reason. */
export function actionAvailability(action: ChatAction, conversation: { type: ConversationType }): { available: true } | { available: false; reason: string } {
  if (action.needsPatient && !composeFeatures(conversation.type).patients) return { available: false, reason: 'Patients cannot be linked in this chat' };
  return { available: true };
}

/** The actions with the recently used first (most recent first), then the rest in their usual order. */
export function recentFirst<A extends { id: string }>(actions: readonly A[], recentIds: readonly string[]): A[] {
  const recent = recentIds.map((id) => actions.find((a) => a.id === id)).filter((a): a is A => !!a);
  return [...recent, ...actions.filter((a) => !recent.includes(a))];
}

/** The recently used list after using `id`: it goes first, once, and the list keeps five. */
export function rememberAction(recentIds: readonly string[], id: string): string[] {
  return [id, ...recentIds.filter((x) => x !== id)].slice(0, 5);
}

const DAY_MS = 86_400_000;
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayOf = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));
const isoOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * The due date a task's text names, as YYYY-MM-DD, or null: "today", "tomorrow", a weekday (the next one, never today),
 * "next week", "in 3 days", "in 2 weeks", or a day/month date (South African order, the next one from today).
 */
export function taskDueFrom(text: string, today: string): string | null {
  const t = text.toLowerCase();
  const base = dayOf(today);
  if (/\btoday\b/.test(t)) return today;
  if (/\btomorrow\b/.test(t)) return isoOf(base + DAY_MS);
  if (/\bnext week\b/.test(t)) return isoOf(base + 7 * DAY_MS);
  const within = /\bin (\d{1,2}) (day|days|week|weeks)\b/.exec(t);
  if (within) return isoOf(base + +within[1]! * (within[2]!.startsWith('week') ? 7 : 1) * DAY_MS);
  const weekday = /\b(sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)(?:day|nesday|rsday|urday|sday)?\b/.exec(t);
  if (weekday) {
    const want = WEEKDAYS.indexOf(weekday[1]!.slice(0, 3));
    const now = new Date(base).getUTCDay();
    return isoOf(base + (((want - now + 6) % 7) + 1) * DAY_MS);
  }
  const date = /(?:^|\s)(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?(?=\s|$|[.,])/.exec(t);
  if (date) {
    const d = +date[1]!;
    const m = +date[2]!;
    if (d < 1 || d > 31 || m < 1 || m > 12) return null;
    let year = date[3] ? +date[3] : new Date(base).getUTCFullYear();
    if (!date[3] && Date.UTC(year, m - 1, d) < base) year += 1;
    return isoOf(Date.UTC(year, m - 1, d));
  }
  return null;
}

const shortDate = (iso: string) => `${+iso.slice(8, 10)} ${MONTHS[+iso.slice(5, 7) - 1]}`;

/** The card's line under its title: who did it once done; while open, when it is due and who has it. Null for neither. */
export function actionCardStatus(card: ActionCard, today: string): string | null {
  if (card.type === 'appointment') return appointmentStatusLine(card, today);
  if (card.type === 'note') return card.noteType ?? null;
  if (card.status === 'done') return card.doneBy ? `Done by ${card.doneBy}` : 'Done';
  const parts: string[] = [];
  if (card.due) {
    const days = Math.round((dayOf(card.due) - dayOf(today)) / DAY_MS);
    parts.push(days < 0 ? `Overdue since ${shortDate(card.due)}` : days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : `Due ${shortDate(card.due)}`);
  }
  if (card.assignees?.length) parts.push(card.assignees.join(', '));
  return parts.length ? parts.join(' · ') : null;
}

/** "Make a task" from a message: its text without the `#patients` it names, with the spaces folded. */
export function messageTaskText(body: string, patientTitles: readonly string[]): string {
  let text = body;
  for (const title of patientTitles) text = text.split(`#${title}`).join(' ');
  return text.replace(/\s+/g, ' ').trim();
}

const WEEKDAY_NAMES = ['Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed']; // day 0 (1970-01-01) was a Thursday
const STATUS_WORDS: Record<AppointmentStatus, string> = { booked: 'Booked', confirmed: 'Confirmed', arrived: 'Arrived', completed: 'Completed', cancelled: 'Cancelled', did_not_attend: 'Did not attend' };

/** A booking's line: when (today and tomorrow by name), with whom, and how it stands; a cancelled one names who. */
function appointmentStatusLine(card: ActionCard, today: string): string | null {
  const parts: string[] = [];
  if (card.start) {
    const day = card.start.slice(0, 10);
    const time = card.start.slice(11, 16);
    const days = Math.round((dayOf(day) - dayOf(today)) / DAY_MS);
    const named = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${WEEKDAY_NAMES[((dayOf(day) / DAY_MS) % 7 + 7) % 7]} ${shortDate(day)}`;
    parts.push(`${named} ${time}`);
  }
  const status = (card.status ?? 'booked') as AppointmentStatus;
  if (status === 'cancelled') {
    parts.push(card.doneBy ? `Cancelled by ${card.doneBy}` : 'Cancelled');
    return parts.join(' · ');
  }
  if (card.doctor) parts.push(card.doctor);
  parts.push(STATUS_WORDS[status] ?? 'Booked');
  return parts.join(' · ');
}

const TIME_24 = /(?:^|\s)(?:at\s+)?(\d{1,2})(?::|h)(\d{2})(?=\s|$|[.,])/i;
const TIME_12 = /(?:^|\s)(?:at\s+)?(\d{1,2})\s?(am|pm)(?=\s|$|[.,])/i;

/** The time a booking's text names, HH:mm: "10:00", "14h30", "2pm", "at 9am"; null for none or a time that is not one. */
function timeFrom(text: string): string | null {
  const t24 = TIME_24.exec(text);
  if (t24) {
    const h = Number(t24[1]);
    const m = Number(t24[2]);
    return h < 24 && m < 60 ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` : null;
  }
  const t12 = TIME_12.exec(text);
  if (t12) {
    const h = Number(t12[1]);
    if (h < 1 || h > 12) return null;
    const hour = (h % 12) + (t12[2]!.toLowerCase() === 'pm' ? 12 : 0);
    return `${String(hour).padStart(2, '0')}:00`;
  }
  return null;
}

/** The day and time a booking's text names: a time alone is today; a day alone has no time. */
export function appointmentWhenFrom(text: string, today: string): { date?: string; time?: string } {
  const time = timeFrom(text);
  const date = taskDueFrom(text, today) ?? (time ? today : null);
  return { ...(date ? { date } : {}), ...(time ? { time } : {}) };
}

const WHEN_WORDS = [
  /(?:^|\s)(?:on\s+)?(?:today|tomorrow|next week)(?=\s|$|[.,])/gi,
  /(?:^|\s)(?:on\s+)?(?:sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)(?:day|nesday|rsday|urday|sday)?(?=\s|$|[.,])/gi,
  /(?:^|\s)in \d{1,2} (?:day|days|week|weeks)(?=\s|$|[.,])/gi,
  /(?:^|\s)(?:on\s+)?\d{1,2}\/\d{1,2}(?:\/\d{4})?(?=\s|$|[.,])/gi,
  /(?:^|\s)(?:at\s+)?\d{1,2}(?::|h)\d{2}(?=\s|$|[.,])/gi,
  /(?:^|\s)(?:at\s+)?\d{1,2}\s?(?:am|pm)(?=\s|$|[.,])/gi,
];

/** A booking's reason: its text without the day and time it names. */
export function withoutWhen(text: string): string {
  let out = text;
  for (const pattern of WHEN_WORDS) out = out.replace(pattern, ' ');
  return out.replace(/\s+/g, ' ').trim();
}

/**
 * "Add to notes" from the chat: a message, or the run of messages under a patient heading, as the note's draft, one line
 * each: who, when (as [timeOf] words it), what. Messages without text are left out.
 */
export function noteDraftFrom(
  messages: readonly { sender?: { name: string } | null; createdAt: string; body?: string | null }[],
  timeOf: (iso: string) => string,
): string {
  return messages
    .filter((m) => (m.body ?? '').trim())
    .map((m) => `${m.sender?.name ? `${m.sender.name}, ` : ''}${timeOf(m.createdAt)}: ${(m.body ?? '').trim()}`)
    .join('\n');
}

/**
 * The actions a draft names, in the order they were typed, each once. Each is a word of its own ("/task" at the start
 * or after a space, then a space or the end), so "and/or" and "12/10" name none. Only the first runs when it is sent.
 */
export function actionsInDraft(draft: string, actions: readonly ChatAction[]): ChatAction[] {
  const escape = (label: string) => label.replace(/[.*+?^$()|[\]\\{}]/g, '\\$&');
  return actions
    .map((a) => ({ a, at: draft.search(new RegExp('(^|\\s)' + escape(a.label) + '(?=\\s|$)')) }))
    .filter((x) => x.at >= 0)
    .sort((x, y) => x.at - y.at)
    .map((x) => x.a);
}

/** What to tell the person when their message named more actions than the one that ran; null when it named one. */
export function extraActionsNotice(ran: ChatAction, others: readonly ChatAction[]): string | null {
  if (!others.length) return null;
  const names = others.map((a) => a.label);
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Only ${ran.label} ran: one action runs per message. ${list} did not run.`;
}

/** Lowercase words of letters and digits: "Follow-up" is "follow", "up". */
const wordsOf = (text: string) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/** Whether a typed word means a type's word: the same, one letter off (five letters or more), or its start ("emerg"). */
function sameWord(typed: string, word: string): boolean {
  if (typed === word) return true;
  if (typed.length >= 4 && word.startsWith(typed)) return true;
  if (Math.min(typed.length, word.length) < 5 || Math.abs(typed.length - word.length) > 1) return false;
  // two neighbours swapped ("theater", "theatre") count as one slip
  if (typed.length === word.length) {
    const diff = [...typed].map((c, k) => (c === word[k] ? -1 : k)).filter((k) => k >= 0);
    if (diff.length === 2 && diff[1] === diff[0]! + 1 && typed[diff[0]!] === word[diff[1]!] && typed[diff[1]!] === word[diff[0]!]) return true;
  }
  // one edit: a letter changed, added or left out
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < typed.length && j < word.length) {
    if (typed[i] === word[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (typed.length > word.length) i++;
    else if (typed.length < word.length) j++;
    else { i++; j++; }
  }
  return edits + (typed.length - i) + (word.length - j) <= 1;
}

/**
 * The practice's appointment type a booking's text names, and the text left as its reason: every word of the type's
 * name must appear (ignoring case and hyphens, forgiving one slip or a shortened word); the type matching the most
 * words wins. Null when none fits, so nothing is guessed.
 */
export function closestAppointmentType(text: string, types: readonly { id: string; name: string }[]): { id: string; reason: string } | null {
  const typed = wordsOf(text);
  let best: { id: string; used: number[]; size: number } | null = null;
  for (const t of types) {
    const want = wordsOf(t.name);
    if (!want.length) continue;
    const used: number[] = [];
    for (const w of want) {
      const at = typed.findIndex((x, i) => !used.includes(i) && sameWord(x, w));
      if (at < 0) break;
      used.push(at);
    }
    if (used.length !== want.length) continue;
    if (!best || want.length > best.size) best = { id: t.id, used, size: want.length };
  }
  if (!best) return null;
  const kept = typed.filter((_, i) => !best!.used.includes(i));
  // the reason keeps the person's own words, without the ones that named the type
  const reason = text.split(/\s+/).filter((piece) => {
    const parts = wordsOf(piece);
    return parts.length === 0 || parts.some((p) => kept.includes(p));
  }).join(' ').trim();
  return { id: best.id, reason };
}
